import {ReadonlySectionHeader} from '@github-ui/issue-metadata/ReadonlySectionHeader'
import {Section} from '@github-ui/issue-metadata/Section'
import {Button} from '@primer/react'
import {Dialog} from '@primer/react/experimental'
import {Suspense, useState} from 'react'
import {graphql, useFragment} from 'react-relay'

import {LABELS} from '../../constants/labels'
import type {ParticipantsSectionFragment$key} from './__generated__/ParticipantsSectionFragment.graphql'
import {Participant} from './Participant'
import {ParticipantsList} from './ParticipantsList'
import {ParticipantsListLoading} from './ParticipantsListLoading'
import styles from './ParticipantsSection.module.css'

type ParticipantsSectionProps = {
  issue: ParticipantsSectionFragment$key
}

export function ParticipantsSection({issue}: ParticipantsSectionProps) {
  const {
    repository: {
      owner: {login: owner},
      name: repo,
      viewerCanPinIssues,
    },
    number,
    participants,
    viewerCanConvertToDiscussion,
    viewerCanDelete,
    viewerCanTransfer,
    viewerCanType,
    viewerCanLock,
  } = useFragment(
    graphql`
      fragment ParticipantsSectionFragment on Issue {
        ... on Issue {
          repository {
            name
            owner {
              login
            }
            viewerCanPinIssues
          }
          number
          participants(first: 10) {
            nodes {
              ...ParticipantFragment
              id
            }
            totalCount
          }
          viewerCanConvertToDiscussion
          viewerCanDelete
          viewerCanTransfer
          viewerCanType
          viewerCanLock
        }
      }
    `,
    issue,
  )

  const hideDivider =
    !viewerCanLock &&
    !viewerCanTransfer &&
    !viewerCanDelete &&
    !viewerCanPinIssues &&
    !viewerCanConvertToDiscussion &&
    !viewerCanType

  const filteredParticipants = (participants.nodes || []).flatMap(a => (a ? a : [])).slice(0, 5)
  const [isOpen, setIsOpen] = useState(false)

  const hasParticipants = filteredParticipants.length > 0
  const showMoreCount = participants.totalCount - 5
  const showMoreAriaLabel = showMoreCount
    ? `and ${showMoreCount} other participants, click to see the full list`
    : undefined

  return (
    <Section
      sectionHeader={<ReadonlySectionHeader title={LABELS.sectionTitles.participants} />}
      emptyText={hasParticipants ? undefined : LABELS.emptySections.participants}
      hideDivider={hideDivider}
    >
      <div data-testid="sidebar-participants-section" className={styles.participantsContainer}>
        <div className={styles.participantsListContainer}>
          {filteredParticipants.map(participant => (
            <Participant key={participant.id} participant={participant} />
          ))}
        </div>
        {showMoreCount > 0 && (
          <Button
            aria-label={showMoreAriaLabel}
            variant="invisible"
            onClick={() => setIsOpen(true)}
            className={styles.showMoreButton}
          >
            +{showMoreCount}
          </Button>
        )}
        {isOpen && (
          <Dialog title="Participants" onClose={() => setIsOpen(false)}>
            <div className={styles.participantsDialogContent}>
              <Suspense fallback={<ParticipantsListLoading />}>
                <ParticipantsList owner={owner} repo={repo} number={number} totalCount={participants.totalCount} />
              </Suspense>
            </div>
          </Dialog>
        )}
      </div>
    </Section>
  )
}

try{ ParticipantsSection.displayName ||= 'ParticipantsSection' } catch {}